import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setNeedRestartRandomizer } from '../../containers/App/actions';
import {
  getRandomizerSettingsLocalStorage,
  setRandomizerSettingsLocalStorage,
} from '../../localStorage/randomizerSettingsLocalStorage';
import V2Icon from '../components/V2Icon';

type FormValues = {
  minRange: string;
  maxRange: string;
  delay: string;
};

type ValidationKey =
  | 'required'
  | 'settings_randomizer_min_error'
  | 'settings_randomizer_max_error'
  | 'settings_randomizer_delay_error'
  | 'max_range_must_be_greater_than_min_range';

const defaults: FormValues = { minRange: '0', maxRange: '100', delay: '5' };
const maxDelaySeconds = 2_147_483;

const isIntegerInRange = (value: string, min: number, max: number) => {
  const number = Number(value);
  return /^\d+$/.test(value) && Number.isSafeInteger(number) && number >= min && number <= max;
};

const validateValues = (values: FormValues) => {
  const errors: Partial<Record<keyof FormValues, ValidationKey>> = {};

  if (!values.minRange) {
    errors.minRange = 'required';
  } else if (!isIntegerInRange(values.minRange, 0, 99)) {
    errors.minRange = 'settings_randomizer_min_error';
  }

  if (!values.maxRange) {
    errors.maxRange = 'required';
  } else if (!isIntegerInRange(values.maxRange, 1, 100)) {
    errors.maxRange = 'settings_randomizer_max_error';
  } else if (!errors.minRange && Number(values.maxRange) <= Number(values.minRange)) {
    errors.maxRange = 'max_range_must_be_greater_than_min_range';
  }

  if (!values.delay) {
    errors.delay = 'required';
  } else if (!isIntegerInRange(values.delay, 1, maxDelaySeconds)) {
    errors.delay = 'settings_randomizer_delay_error';
  }

  return errors;
};

const readInitialValues = (): FormValues => {
  try {
    const stored: unknown = getRandomizerSettingsLocalStorage();
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
      return { ...defaults };
    }

    const record = stored as Partial<Record<keyof FormValues, unknown>>;
    const readNumber = (value: unknown) =>
      typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    const values = {
      minRange: readNumber(record.minRange),
      maxRange: readNumber(record.maxRange),
      delay: readNumber(record.delay),
    };

    if (Object.keys(validateValues(values)).length > 0) {
      return { ...defaults };
    }

    return {
      minRange: String(Number(values.minRange)),
      maxRange: String(Number(values.maxRange)),
      delay: String(Number(values.delay)),
    };
  } catch {
    return { ...defaults };
  }
};

const V2RandomizerSettings: React.FC = () => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const id = React.useId();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [values, setValues] = React.useState<FormValues>(readInitialValues);
  const [validationRequested, setValidationRequested] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const errors = validationRequested ? validateValues(values) : {};

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationRequested(true);
    setSaved(false);
    const nextErrors = validateValues(values);
    const invalidField = (Object.keys(nextErrors) as (keyof FormValues)[])[0];

    if (invalidField) {
      const input = formRef.current?.elements.namedItem(invalidField);
      if (input instanceof HTMLInputElement) {
        input.focus();
      }
      return;
    }

    const nextValues = {
      minRange: String(Number(values.minRange)),
      maxRange: String(Number(values.maxRange)),
      delay: String(Number(values.delay)),
    };
    setRandomizerSettingsLocalStorage(nextValues.minRange, nextValues.maxRange, nextValues.delay);
    dispatch(setNeedRestartRandomizer(true));
    setValues(nextValues);
    setSaved(true);
  };

  const fields = [
    { name: 'minRange', label: t('range_from'), min: 0, max: 99 },
    { name: 'maxRange', label: t('range_to'), min: 1, max: 100 },
    {
      name: 'delay',
      label: t('settings_randomizer_delay'),
      min: 1,
      max: maxDelaySeconds,
    },
  ] as const;

  return (
    <form
      ref={formRef}
      className="v2-settings-panel"
      aria-labelledby={`${id}-title`}
      noValidate
      onSubmit={handleSubmit}
    >
      <div className="v2-settings-panel__head">
        <span className="v2-settings-panel__icon">
          <V2Icon name="dice" />
        </span>
        <div>
          <h2 id={`${id}-title`}>{t('randomizer')}</h2>
          <p className="v2-settings-panel__description">{t('settings_randomizer_description')}</p>
        </div>
      </div>

      <div className="v2-settings-fields-row">
        {fields.map(({ name, label, min, max }) => {
          const error = errors[name];
          return (
            <div className="v2-settings-field" key={name}>
              <label htmlFor={`${id}-${name}`}>{label}</label>
              <input
                className="v2-settings-input"
                id={`${id}-${name}`}
                name={name}
                type="number"
                inputMode="numeric"
                autoComplete="off"
                min={min}
                max={max}
                step={1}
                required
                value={values[name]}
                aria-invalid={Boolean(error) || undefined}
                aria-describedby={error ? `${id}-${name}-error` : undefined}
                onChange={(event) => {
                  setValues((current) => ({ ...current, [name]: event.target.value }));
                  setSaved(false);
                }}
              />
              {error && (
                <p className="v2-settings-field__error" id={`${id}-${name}-error`} role="alert">
                  {t(error, { max: maxDelaySeconds.toLocaleString() })}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="v2-settings-panel__footer">
        <p className="v2-settings-status" role="status">
          {saved ? t('settings_saved') : ''}
        </p>
        <button
          className="outline-button"
          type="button"
          onClick={() => {
            setValues(defaults);
            setValidationRequested(false);
            setSaved(false);
          }}
        >
          {t('reset')}
        </button>
        <button className="acid-button" type="submit">
          {t('apply')}
        </button>
      </div>
    </form>
  );
};

export default V2RandomizerSettings;
